// The client's Quote at /o/:uuid — CEO redesign 2026-10-15 ("Opportunity →
// Quote" brief; design ref other-quote-review…/artwork.html).
//
// One page for the whole buying journey: the client shops (several items,
// pricing shown or not), reviews each item's artwork + decoration details
// (location, method, size, colour), requests changes, approves — then, once
// the order is live, pays and tracks it here too.
//
// Rules (CEO 2026-10-15):
//   - Artwork is managed in Flow only. The client previews it; never uploads.
//   - Flow controls whether prices/totals show and whether the approval
//     section shows. Those switches never appear on this page — the server
//     sends only the result (hidden prices aren't in the response at all).
//   - Approve sends back `reviewVersion`, the fingerprint of exactly what the
//     page shows; the server refuses (409 quote_changed) if the quote moved,
//     and keeps a copy of the approved version.
//
// Anonymous-by-default — the share UUID is the gate. Staff with a valid JWT
// get a draft-preview pass via the backend's staffPreview flag.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ApiError, api } from "@/lib/api";
import { addressSnapshotLines } from "@/lib/address-snapshot";
import { DocumentPrintStyles } from "@/components/documents/PrintStyles";
import { lineTotalCents } from "@/components/documents/money";
import {
  chargeQuantity,
  groupLinesForDocument,
  isSalesOrderStage,
  isSoSignoffReachable,
  lineChargesCents,
} from "@merchd/shared";
import type { DocSection } from "@/components/documents/types";
import { carrierLabel } from "@/lib/carriers";
import { useAuth } from "@/auth/AuthProvider";

interface AddressSnapshot {
  [k: string]: unknown;
}

interface PublicOrder {
  id: string;
  orderNumber: number;
  statusStage: string;
  statusState: string;
  terms: string;
  inHandsDate: string | null;
  introduction: string | null;
  customerPo: string | null;
  currency: string | null;
  billingAddressSnapshot: AddressSnapshot | null;
  shippingAddressSnapshot: AddressSnapshot | null;
  hidePricing: boolean;
  termsAndConditions: string | null;
  requiresUpfrontPayment?: boolean;
  upfrontDueCents?: number;
  ccFeeCents?: number;
  feeIncluded?: boolean;
  ccSurchargePct?: number;
  upfrontPaymentStatus?: "not_required" | "pending" | "paid";
  upfrontPaidAt?: string | null;
  /** The client's sign-off once the order is live (committed or later). */
  soApprovedAt?: string | null;
  soApprovedByName?: string | null;
  /** Approval while still a quote (stage quote). */
  approvedAt?: string | null;
  approvedByName?: string | null;
  /** How many times the quote has been sent — "version N". */
  quoteVersion?: number;
  /** Fingerprint of what this page shows; Approve sends it back. */
  reviewVersion?: string;
  /** Flow's "Approve button" switch (always true until the DB step runs). */
  showApproval?: boolean;
}

interface PublicShipment {
  id: string;
  status: "pending" | "in_transit" | "out_for_delivery" | "delivered" | "exception" | "returned";
  carrier: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  projectedShipDate: string | null;
}

interface PublicLineVariant {
  id: string;
  color: string | null;
  size: string | null;
  material: string | null;
  quantity: number;
  /** null when Flow hides pricing. */
  unitPrice: string | null;
}

interface PublicCharge {
  id?: string;
  chargeType?: string;
  note?: string | null;
  amount: string;
  billingModel?: string | null;
  /** How many (null = default: fixed 1, per piece the units). */
  quantity?: number | null;
}

interface PublicDecoration {
  id: string;
  location: string | null;
  colorCount: number | null;
  method: string | null;
  size: string | null;
  color: string | null;
  isItemCosts?: boolean;
  charges: PublicCharge[];
  artwork: { url: string; name: string; isImage: boolean; previewUrl?: string | null } | null;
}

interface PublicLine {
  id: string;
  lineNumber: number;
  serviceType: string;
  description: string | null;
  productSnapshot: {
    name?: string;
    description?: string;
    imageUrls?: string[] | Record<string, string>;
    categories?: unknown;
    sku?: string;
  } | null;
  /** null when Flow hides pricing. */
  unitPrice: string | null;
  quantityTotal: number;
  sectionId?: string | null;
  variants: PublicLineVariant[];
  decorations: PublicDecoration[];
}

interface RemainingBalance {
  invoiceShareUuid: string;
  netTotalCents: number;
  outstandingCents: number;
  paidAmountCents: number;
  ccFeeCents: number;
  feeIncluded: boolean;
  ccSurchargePct: number;
  chargeCents: number;
  payable: boolean;
  paid: boolean;
  paidAt: string | null;
}

interface PublicOrderResponse {
  order: PublicOrder;
  lines: PublicLine[];
  sections?: DocSection[];
  totals: { subtotal: string; tax: string; total: string } | null;
  shipments?: PublicShipment[];
  remainingBalance?: RemainingBalance | null;
  client: { id: string; name: string; projectName: string; contactName?: string | null } | null;
  sentBy: { name: string | null; email: string | null; phone: string | null } | null;
  staffPreview?: boolean;
}

const DEFAULT_TERMS_AND_CONDITIONS = `Setup costs apply per imprint location unless otherwise noted.
Standard production lead times are 7–10 business days after artwork approval. Rush options are available — please contact your rep for details.
The applicable tax rate may be updated based on your shipping address.`;
// The 30-day pricing guarantee line was removed (Stephanie 2026-11-02).

const TERMS_LABEL: Record<string, string> = {
  net_15: "Net 15",
  net_30: "Net 30",
  net_45: "Net 45",
  net_60: "Net 60",
  net_90: "Net 90",
  credit_card: "Credit card",
  upfront: "Paid upfront",
};

const SHIPMENT_STATUS_LABEL: Record<PublicShipment["status"], string> = {
  pending: "Preparing to ship",
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  exception: "Delivery issue — your rep is on it",
  returned: "Returned",
};

function money(n: number | string | null | undefined, currency: string): string {
  const num = typeof n === "string" ? Number(n) : n ?? NaN;
  if (!Number.isFinite(num)) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD" }).format(num);
  } catch {
    return `$${num.toFixed(2)}`;
  }
}

function cents(c: number, currency: string): string {
  return money(c / 100, currency);
}

function longDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00`) : new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function firstImage(snapshot: PublicLine["productSnapshot"]): string | null {
  const urls = snapshot?.imageUrls;
  if (!urls) return null;
  if (Array.isArray(urls)) return urls.find((u) => typeof u === "string" && u) ?? null;
  return Object.values(urls).find((u) => typeof u === "string" && u) ?? null;
}

function categoryOf(snapshot: PublicLine["productSnapshot"]): string | null {
  const c = snapshot?.categories;
  if (!Array.isArray(c) || c.length === 0) return null;
  const first = c[0] as unknown;
  if (typeof first === "string") return first;
  if (first && typeof first === "object" && "name" in first && typeof (first as { name: unknown }).name === "string") {
    return (first as { name: string }).name;
  }
  return null;
}

function lineName(l: PublicLine): string {
  return l.productSnapshot?.name?.trim() || l.description?.split("\n")[0]?.trim() || `Item ${l.lineNumber}`;
}

function lineBody(l: PublicLine): string | null {
  const name = lineName(l);
  const d = (l.description ?? "").trim();
  if (d && d !== name) return d;
  return l.productSnapshot?.description?.trim() || null;
}

/** "Black — S 10 · M 20" per colour, in the order the rep entered them. */
function variantSummary(variants: PublicLineVariant[]): string[] {
  const byColor = new Map<string, string[]>();
  for (const v of variants) {
    if (!v.quantity) continue;
    const key = v.color?.trim() || v.material?.trim() || "";
    const part = v.size?.trim() ? `${v.size.trim()} ${v.quantity}` : `${v.quantity}`;
    byColor.set(key, [...(byColor.get(key) ?? []), part]);
  }
  return [...byColor.entries()].map(([color, parts]) => (color ? `${color} — ${parts.join(" · ")}` : parts.join(" · ")));
}

function chargeLabel(c: PublicCharge): string {
  if (c.note && c.note.trim()) return c.note.trim();
  const map: Record<string, string> = {
    setup: "Setup",
    run: "Run charge",
    digitizing: "Digitizing",
    rush: "Rush",
    screen: "Screen charge",
    pms_match: "PMS match",
  };
  return map[c.chargeType ?? ""] ?? "Additional charge";
}

function toMoneyLine(l: PublicLine) {
  return {
    unitPrice: l.unitPrice ?? "0",
    quantityTotal: l.quantityTotal,
    serviceType: l.serviceType,
    variants: l.variants.map((v) => ({ quantity: v.quantity, unitPrice: v.unitPrice })),
    decorationCharges: l.decorations.flatMap((d) =>
      d.charges.map((c) => ({ amount: c.amount, billingModel: c.billingModel === "per_piece" ? ("per_piece" as const) : ("fixed" as const), quantity: c.quantity ?? null })),
    ),
  };
}

/** Price each for the goods: one price, or "from" when colours/sizes differ. */
function unitPriceLabel(l: PublicLine, currency: string): string | null {
  if (l.unitPrice == null) return null;
  const prices = l.variants.filter((v) => v.quantity > 0).map((v) => Number(v.unitPrice ?? l.unitPrice));
  if (prices.length === 0) return money(l.unitPrice, currency);
  const lo = Math.min(...prices);
  const hi = Math.max(...prices);
  return lo === hi ? money(lo, currency) : `from ${money(lo, currency)}`;
}

function initials(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] ?? "O").toUpperCase() + (parts[1]?.[0] ?? "").toUpperCase();
}

export function PublicOrderPage(props: { previewUuid?: string } = {}) {
  // previewUuid: shown inside the Send window as a look-only preview
  // (Stephanie 2026-10-26) instead of from the /o/:uuid route.
  const params = useParams<{ uuid: string }>();
  const uuid = props.previewUuid ?? params.uuid ?? "";
  const embedded = props.previewUuid !== undefined;
  const [data, setData] = useState<PublicOrderResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ tone: "ok" | "warn"; text: string } | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [approverName, setApproverName] = useState("");
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeText, setChangeText] = useState("");
  const [changeName, setChangeName] = useState("");
  const [preview, setPreview] = useState<PublicDecoration | null>(null);
  const changeDialog = useRef<HTMLDialogElement>(null);
  const noticeRef = useRef<HTMLParagraphElement>(null);
  const previewDialog = useRef<HTMLDialogElement>(null);
  // Wait for AuthProvider so a logged-in rep's staff token rides the fetch
  // (draft preview); anonymous clients send no header.
  const { loading: authLoading } = useAuth();

  const load = useCallback(async () => {
    try {
      const res = await api<PublicOrderResponse>(`/o/${uuid}`, { skipAuthRefresh: true });
      setData(res);
      setLoadError(null);
      if (!embedded) document.title = `Quote #${res.order.orderNumber} · other`;
    } catch (err) {
      setLoadError(
        err instanceof ApiError && err.status === 404
          ? "This quote link is no longer active."
          : "Couldn't load this quote. Try again later.",
      );
    }
  }, [uuid, embedded]);

  useEffect(() => {
    if (authLoading) return;
    void load();
  }, [authLoading, load]);

  // The PDF is the page printed: open every collapsed section first so the
  // terms and artwork details are on paper, not just their headings.
  useEffect(() => {
    const openAll = () => document.querySelectorAll(".oq details").forEach((d) => ((d as HTMLDetailsElement).open = true));
    window.addEventListener("beforeprint", openAll);
    return () => window.removeEventListener("beforeprint", openAll);
  }, []);

  // A result the client must see (approved, change sent, refused) — bring it
  // into view; they clicked from the bottom of a long page.
  useEffect(() => {
    if (notice) noticeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [notice]);

  useEffect(() => {
    const d = changeDialog.current;
    if (!d) return;
    if (changeOpen && !d.open) d.showModal();
    if (!changeOpen && d.open) d.close();
  }, [changeOpen]);
  useEffect(() => {
    const d = previewDialog.current;
    if (!d) return;
    if (preview && !d.open) d.showModal();
    if (!preview && d.open) d.close();
  }, [preview]);

  const lines = useMemo(() => data?.lines ?? [], [data]);
  const sections = useMemo(() => data?.sections ?? [], [data]);
  const groups = useMemo(() => groupLinesForDocument(lines, sections, { editable: false }), [lines, sections]);

  function friendlyError(err: unknown, fallback: string): string {
    if (err instanceof ApiError && err.status === 409) {
      const code = (err.body as { error?: string } | undefined)?.error;
      if (code === "quote_changed") return "This quote was just updated. Reload the page to review the latest version before approving.";
      return "This quote's options changed. Reload the page to see what's current.";
    }
    return fallback;
  }

  async function approve(): Promise<void> {
    if (!data) return;
    setSaving(true);
    setNotice(null);
    const body = JSON.stringify({
      approved_by_name: approverName.trim() || undefined,
      review_version: data.order.reviewVersion,
    });
    try {
      if (data.order.requiresUpfrontPayment === true) {
        const res = await api<{ payment_url: string }>(`/o/${uuid}/approve-and-pay`, { method: "POST", body, skipAuth: true });
        if (res.payment_url) {
          window.location.assign(res.payment_url);
          return;
        }
        setNotice({ tone: "warn", text: "Couldn't start payment — please try again." });
      } else {
        await api(`/o/${uuid}/approve`, { method: "POST", body, skipAuth: true });
        setNotice({ tone: "ok", text: "Approved — thank you. Your account manager has been told and will take it from here." });
        await load();
      }
    } catch (err) {
      setNotice({ tone: "warn", text: friendlyError(err, "Couldn't save your approval — please try again.") });
    } finally {
      setSaving(false);
    }
  }

  async function requestChange(): Promise<void> {
    setSaving(true);
    try {
      await api(`/o/${uuid}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason: changeText.trim() || undefined, rejected_by_name: changeName.trim() || undefined }),
        skipAuth: true,
      });
      setChangeOpen(false);
      setChangeText("");
      setNotice({ tone: "ok", text: "Got it — your account manager has your note and will send an updated quote." });
      await load();
    } catch (err) {
      setNotice({ tone: "warn", text: friendlyError(err, "Couldn't send your note — please try again, or reply to your rep's email.") });
      setChangeOpen(false);
    } finally {
      setSaving(false);
    }
  }

  async function payRemainingBalance(): Promise<void> {
    const rb = data?.remainingBalance;
    if (!rb) return;
    setSaving(true);
    try {
      const res = await api<{ url: string }>(`/i/${rb.invoiceShareUuid}/pay`, { method: "POST", body: JSON.stringify({}), skipAuth: true });
      if (res.url) {
        window.location.assign(res.url);
        return;
      }
      setNotice({ tone: "warn", text: "Couldn't start payment — please try again." });
    } catch (err) {
      setNotice({
        tone: "warn",
        text: err instanceof ApiError && err.status === 409
          ? "This balance can't be paid right now — please refresh, or reply to your rep's email."
          : "Couldn't start payment — please try again.",
      });
    } finally {
      setSaving(false);
    }
  }

  if (loadError) {
    return (
      <div className="oq">
        <style>{QUOTE_CSS}</style>
        <main className="oq-empty">
          <h1>Quote not available</h1>
          <p>{loadError}</p>
          <p className="fine">If you think this is a mistake, reply to the email you received with the link.</p>
        </main>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="oq">
        <style>{QUOTE_CSS}</style>
        <main className="oq-empty"><p>Loading your quote…</p></main>
      </div>
    );
  }

  const { order, totals, client, sentBy } = data;
  const currency = order.currency ?? "USD";
  const pricing = !order.hidePricing && totals != null;
  const remainingBalance = data.remainingBalance ?? null;
  const shipments = data.shipments ?? [];
  const isLive = isSalesOrderStage(order.statusStage);
  const requiresUpfront = order.requiresUpfrontPayment === true;
  const upfrontPaid = order.upfrontPaymentStatus === "paid";
  const approvedAt = order.soApprovedAt ?? (order.statusStage === "quote" && order.statusState === "approved" ? order.approvedAt : null);
  const approvedBy = order.soApprovedByName ?? order.approvedByName ?? null;
  const isApproved = approvedAt != null || (isLive && upfrontPaid);
  const closed = order.statusStage === "closed" || order.statusStage === "cancelled";
  // Can the client approve right now? Server rules: a sent quote approves (and
  // goes live); a live order the client hasn't signed off records the sign-off;
  // an upfront order approves through payment.
  // Exactly the server's client_approved states. After a change request the
  // rep re-sends the revised quote, which puts it back to "sent".
  const quoteApprovable = order.statusStage === "quote" && ["sent", "viewed"].includes(order.statusState);
  const liveApprovable = isSoSignoffReachable(order.statusStage) && !order.soApprovedAt;
  const finalInvoiceExists = remainingBalance != null;
  const upfrontPayable = isLive && requiresUpfront && !upfrontPaid && !finalInvoiceExists;
  const canApprove =
    order.showApproval !== false &&
    !isApproved &&
    !closed &&
    (requiresUpfront ? quoteApprovable || upfrontPayable : quoteApprovable || liveApprovable);
  const canRequestChange = !closed && !isApproved && order.statusState !== "changes_requested";
  const showApprovalSection = order.showApproval !== false && (canApprove || isApproved);

  const productGroups = groups
    .map((g) => ({ ...g, lines: g.items.filter((l) => l.serviceType === "product") }))
    .filter((g) => g.lines.length > 0);
  const products = productGroups.flatMap((g) => g.lines);
  const serviceLines = lines.filter((l) => l.serviceType !== "product");
  const units = products.reduce((n, l) => n + (l.quantityTotal || 0), 0);
  const shippingCents = pricing
    ? serviceLines.filter((l) => l.serviceType === "shipping").reduce((n, l) => n + lineTotalCents(toMoneyLine(l)), 0)
    : 0;
  const otherServiceCents = pricing
    ? serviceLines.filter((l) => l.serviceType !== "shipping").reduce((n, l) => n + lineTotalCents(toMoneyLine(l)), 0)
    : 0;
  const itemsCents = pricing && totals ? Math.round(Number(totals.subtotal) * 100) - shippingCents - otherServiceCents : 0;
  const hasShippingLine = serviceLines.some((l) => l.serviceType === "shipping");

  const ship = addressSnapshotLines(order.shippingAddressSnapshot);
  const bill = addressSnapshotLines(order.billingAddressSnapshot);
  const inHand = longDate(order.inHandsDate);
  const termsText = TERMS_LABEL[order.terms] ?? order.terms.replace(/_/g, " ");
  const preparedFor = client?.contactName || ship?.lines.find((l) => l.startsWith("Attn: "))?.slice(6) || client?.name || null;
  const version = order.quoteVersion ?? 1;

  const badge = closed
    ? order.statusStage === "cancelled" ? "Cancelled" : "Complete"
    : remainingBalance?.paid
      ? "Paid"
      : isApproved
        ? isLive ? "Approved · in progress" : "Approved"
        : order.statusState === "changes_requested"
          ? "Changes requested"
          : "Ready for review";

  return (
    <div className="oq">
      <style>{QUOTE_CSS}</style>
      <DocumentPrintStyles />
      <header className="oq-top">
        <img src="/brand/other-primary-lockup-black.svg" alt="other" />
        <button type="button" className="text" onClick={() => window.print()}>↓ Print / save PDF</button>
      </header>
      <main className="oq-main">
        {data.staffPreview && (
          <p className="oq-banner warn noprint">
            <strong>Preview.</strong> You're seeing this quote as a rep — the client's link works once it's sent.
          </p>
        )}
        {notice && <p ref={noticeRef} className={`oq-banner ${notice.tone} noprint`} role="status">{notice.text}</p>}

        <section className="intro">
          <div>
            <p className="eyebrow">
              {(client?.name ?? "").toUpperCase()}
              <span> / </span>
              QUOTE #{order.orderNumber}
            </p>
            <h1>{client?.projectName || `Quote #${order.orderNumber}`}</h1>
            <p className="sub">{order.introduction?.trim() || "Products, artwork, and details. All in one place."}</p>
          </div>
          <span className="badge">{badge}</span>
        </section>

        <div className="facts">
          <div><span>Requested in-hand date</span><strong>{inHand ?? "To be confirmed"}</strong></div>
          <div><span>Payment terms</span><strong>{termsText}</strong></div>
          <div><span>Prepared for</span><strong>{preparedFor ?? "—"}</strong></div>
          <div>
            {/* No customer PO on a quote (Stephanie 2026-10-26). */}
            {order.customerPo && order.statusStage !== "quote" ? (
              <><span>Your PO</span><strong>{order.customerPo}</strong></>
            ) : pricing ? (
              <><span>Quote currency</span><strong>{currency}</strong></>
            ) : (
              <><span>Quote</span><strong>#{order.orderNumber} · version {version}</strong></>
            )}
          </div>
        </div>

        <section className="billing-shipping" aria-label="Billing and shipping information">
          <AddressBlock label="SHIP TO" address={ship} note={inHand ? `Requested in-hand: ${inHand}` : null} />
          <AddressBlock label="BILL TO" address={bill} note={`Payment terms: ${termsText}`} />
          <p className="addresshelp">
            Need to change an address? {canRequestChange ? "Request a change before approving." : "Reply to your account manager."}
            {/* Stephanie 2026-10-27. */}
            <br />
            Address changes made after your order has shipped will result in additional fees.
          </p>
        </section>

        <div className="layout">
          <div>
            <div className="sectionhead">
              <h2>Your items</h2>
              <span>
                {products.length} {products.length === 1 ? "product" : "products"} · {units.toLocaleString("en-US")} units
              </span>
            </div>

            {productGroups.map((g) => (
              <div key={g.key}>
                {g.title && <h3 className="grouphead">{g.title}</h3>}
                {g.lines.map((l) => (
                  <ProductCard
                    key={l.id}
                    line={l}
                    index={products.indexOf(l) + 1}
                    currency={currency}
                    pricing={pricing}
                    approved={isApproved}
                    onPreview={setPreview}
                  />
                ))}
              </div>
            ))}

            {serviceLines.length > 0 && (
              <div className="product services">
                <p className="eyebrow">SHIPPING &amp; OTHER CHARGES</p>
                {serviceLines.map((l) => (
                  <div className="servicerow" key={l.id}>
                    {/* + the rep's note on a shipping line (Stephanie 2026-10-27). */}
                    <span>
                      {lineName(l)}
                      {lineBody(l) && <span className="servicenote">{lineBody(l)}</span>}
                    </span>
                    {pricing && <strong>{cents(lineTotalCents(toMoneyLine(l)), currency)}</strong>}
                  </div>
                ))}
              </div>
            )}

            {(upfrontPaid || remainingBalance || shipments.length > 0 || (isLive && !finalInvoiceExists && (!requiresUpfront || upfrontPaid))) && (
              <section className="status">
                <p className="eyebrow">YOUR ORDER</p>
                {upfrontPaid && (
                  <div className="statusrow">
                    <span>Upfront payment received{order.upfrontPaidAt ? ` · ${longDate(order.upfrontPaidAt)}` : ""}</span>
                    {(order.upfrontDueCents ?? 0) > 0 && <strong>✓ {cents(order.upfrontDueCents ?? 0, currency)}</strong>}
                  </div>
                )}
                {remainingBalance?.paid && (
                  <div className="statusrow">
                    <span>Balance paid{remainingBalance.paidAt ? ` · ${longDate(remainingBalance.paidAt)}` : ""}</span>
                    {remainingBalance.paidAmountCents > 0 && <strong>✓ {cents(remainingBalance.paidAmountCents, currency)}</strong>}
                  </div>
                )}
                {remainingBalance?.payable && (
                  <div className="paybox">
                    {remainingBalance.paidAmountCents > 0 && (
                      <p className="fine">
                        Balance {cents(remainingBalance.netTotalCents, currency)} − {cents(remainingBalance.paidAmountCents, currency)} already paid = {cents(remainingBalance.outstandingCents, currency)} due
                      </p>
                    )}
                    <div className="statusrow">
                      <span>Amount due</span>
                      <strong className="big">{cents(remainingBalance.chargeCents, currency)}</strong>
                    </div>
                    {remainingBalance.feeIncluded && remainingBalance.ccFeeCents > 0 && (
                      <p className="fine">
                        Includes a {remainingBalance.ccSurchargePct}% card processing fee of {cents(remainingBalance.ccFeeCents, currency)}.
                      </p>
                    )}
                    <button type="button" className="primary noprint" disabled={saving} onClick={() => void payRemainingBalance()}>
                      {saving ? "Starting payment…" : `Pay ${cents(remainingBalance.chargeCents, currency)}`}
                    </button>
                    <p className="fine">Payment is handled on Stripe's secure page.</p>
                  </div>
                )}
                {isLive && !finalInvoiceExists && (!requiresUpfront || upfrontPaid) && (
                  <p className="fine">Your final invoice hasn't been issued yet — your account manager will send it when it's time to pay the balance.</p>
                )}
                {shipments.map((s) => <ShipmentRow key={s.id} shipment={s} />)}
              </section>
            )}

            <section className="terms">
              <h3 className="sectionhead">Production details &amp; terms</h3>
              {(order.termsAndConditions?.trim() || DEFAULT_TERMS_AND_CONDITIONS)
                .split(/\n+/)
                .map((p) => p.replace(/^[·*\s]+/, "").trim())
                .filter(Boolean)
                .map((p, i) => <p key={i}>{p}</p>)}
            </section>
          </div>

          <aside>
            <div className="summary">
              <p className="eyebrow">YOUR QUOTE AT A GLANCE</p>
              {products.length > 1 && (
                <div className="itemnav noprint">
                  <label htmlFor="oq-jump">Jump to a product</label>
                  <select
                    id="oq-jump"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) document.getElementById(e.target.value)?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                  >
                    <option value="">{products.length} products in this quote</option>
                    {products.map((l, i) => (
                      <option key={l.id} value={`item-${l.id}`}>{i + 1}. {lineName(l)}</option>
                    ))}
                  </select>
                </div>
              )}
              {pricing && totals ? (
                <>
                  <div className="money"><span>Items</span><strong>{cents(itemsCents, currency)}</strong></div>
                  {otherServiceCents !== 0 && <div className="money"><span>Other charges</span><strong>{cents(otherServiceCents, currency)}</strong></div>}
                  {hasShippingLine && <div className="money"><span>Shipping</span><strong>{cents(shippingCents, currency)}</strong></div>}
                  <div className="money"><span>Tax</span><strong>{money(totals.tax, currency)}</strong></div>
                  <div className="total">
                    <span>Total <small>{currency}</small></span>
                    <strong>{money(totals.total, currency)}</strong>
                  </div>
                  <p className="shipping">{hasShippingLine ? "Shipping is included above." : "Shipping is not included yet — your account manager will add it."}</p>
                </>
              ) : (
                <p className="shipping">Pricing isn’t included in this review.</p>
              )}
              {requiresUpfront && !upfrontPaid && (order.upfrontDueCents ?? 0) > 0 && (
                <p className="shipping">
                  An upfront payment of <strong>{cents(order.upfrontDueCents ?? 0, currency)}</strong> is due on approval
                  {order.feeIncluded && (order.ccFeeCents ?? 0) > 0 ? ` (includes a ${order.ccSurchargePct ?? 0}% card fee)` : ""}.
                </p>
              )}
              {order.statusStage === "quote" && order.statusState === "changes_requested" && (
                <p className="approvedline">You asked for changes — your account manager will send an updated quote to approve.</p>
              )}
              {canApprove && <a className="primary reviewlink noprint" href="#approval">Review &amp; approve ↓</a>}
              {canRequestChange && (
                <button type="button" className="secondary noprint" onClick={() => setChangeOpen(true)}>Request a change</button>
              )}
              {isApproved && (
                <p className="approvedline">
                  ✓ Approved{approvedBy ? ` by ${approvedBy}` : ""}{approvedAt ? ` · ${longDate(approvedAt)}` : ""}
                </p>
              )}
              <p className="fine">Your review includes the products and artwork shown · version {version}.</p>
            </div>
            {sentBy && (sentBy.name || sentBy.email) && (
              <div className="contact">
                <div className="avatar">{initials(sentBy.name)}</div>
                <div>
                  <span>Your account manager</span>
                  <strong>{sentBy.name}</strong>
                  {sentBy.email && <a href={`mailto:${sentBy.email}`}>{sentBy.email}</a>}
                  {sentBy.phone && <a href={`tel:${sentBy.phone.replace(/[^\d+]/g, "")}`}>{sentBy.phone}</a>}
                </div>
              </div>
            )}
          </aside>
        </div>

        {showApprovalSection && (
          <section id="approval" className={isApproved ? "bottomapproval" : "bottomapproval noprint"}>
            <div>
              <p className="eyebrow">ONE FINAL LOOK</p>
              <h2>{isApproved ? "Approved. Thank you." : "Ready to make it happen?"}</h2>
              <p>
                {isApproved
                  ? "Your account manager has your approval and is taking it from here."
                  : "Review the products, quantities, artwork, and decoration details above."}
              </p>
              <p className="fine">Version {version}. Artwork approval is recorded separately from payment and production release.</p>
            </div>
            <div>
              {isApproved ? (
                <p className="accept">
                  ✓ Approved{approvedBy ? ` by ${approvedBy}` : ""}{approvedAt ? ` on ${longDate(approvedAt)}` : ""}.
                </p>
              ) : (
                <>
                  <label className="namefield">
                    <span>Your name</span>
                    <input
                      type="text"
                      value={approverName}
                      onChange={(e) => setApproverName(e.target.value)}
                      autoComplete="name"
                      maxLength={200}
                    />
                  </label>
                  <label className="accept">
                    <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                    <span>I’ve reviewed this version of the quote, its artwork and decoration details, and the terms.</span>
                  </label>
                  <button
                    type="button"
                    className="primary"
                    disabled={!accepted || approverName.trim().length < 2 || saving}
                    onClick={() => void approve()}
                  >
                    {saving
                      ? "Saving…"
                      : requiresUpfront && (order.upfrontDueCents ?? 0) > 0
                        ? `Approve & pay ${cents(order.upfrontDueCents ?? 0, currency)} ↗`
                        : "Approve quote & artwork ↗"}
                  </button>
                  {(!accepted || approverName.trim().length < 2) && (
                    <p className="fine">Enter your name and tick the box to approve.</p>
                  )}
                  {requiresUpfront && <p className="fine">You'll pay on Stripe's secure page.</p>}
                </>
              )}
            </div>
          </section>
        )}

        <footer>
          <span>Good things. All taken care of.</span>
          <p>other · MERCHD LLC<br />3013 Yamato Rd. B12 Suite 301 · Boca Raton, FL 33434</p>
        </footer>
      </main>

      <dialog ref={changeDialog} onClose={() => setChangeOpen(false)} className="noprint">
        <button type="button" className="close" aria-label="Close" onClick={() => setChangeOpen(false)}>×</button>
        <p className="eyebrow">REQUEST A CHANGE</p>
        <h2>What would you like changed?</h2>
        <p>Tell your account manager what to adjust — items, quantities, artwork, addresses or dates. They'll send an updated quote.</p>
        <label className="namefield">
          <span>Your name</span>
          <input type="text" value={changeName} onChange={(e) => setChangeName(e.target.value)} autoComplete="name" maxLength={200} />
        </label>
        <textarea value={changeText} onChange={(e) => setChangeText(e.target.value)} maxLength={2000} placeholder="e.g. Make the tees 150 instead of 100, and use the white logo on the tumbler." />
        <button type="button" className="primary" disabled={saving || changeText.trim().length === 0} onClick={() => void requestChange()}>
          {saving ? "Sending…" : "Send to my account manager"}
        </button>
      </dialog>

      <dialog ref={previewDialog} onClose={() => setPreview(null)} className="noprint artdialog">
        <button type="button" className="close" aria-label="Close" onClick={() => setPreview(null)}>×</button>
        {preview && (
          <>
            <p className="eyebrow">ARTWORK · {(preview.location ?? "Location").toUpperCase()}</p>
            <h2>{preview.artwork?.name ?? "Artwork"}</h2>
            {preview.artwork?.isImage ? (
              <img className="fullart" src={preview.artwork.url} alt={`Artwork for ${preview.location ?? "this location"}`} />
            ) : preview.artwork?.previewUrl ? (
              <>
                <img className="fullart" src={preview.artwork.previewUrl} alt={`Artwork for ${preview.location ?? "this location"}`} />
                <p><a href={preview.artwork.url} target="_blank" rel="noreferrer">Open the file ↗</a></p>
              </>
            ) : preview.artwork ? (
              <p><a href={preview.artwork.url} target="_blank" rel="noreferrer">Open the file ↗</a></p>
            ) : null}
            <dl className="artdl">
              <div><dt>Method</dt><dd>{preview.method ?? "To be confirmed"}</dd></div>
              <div><dt>Size</dt><dd>{preview.size ?? "To be confirmed"}</dd></div>
              <div><dt>Color / finish</dt><dd>{preview.color ?? "To be confirmed"}</dd></div>
            </dl>
            <p className="fine">Something not right? Close this and use “Request a change”.</p>
          </>
        )}
      </dialog>
    </div>
  );
}

function AddressBlock(props: { label: string; address: { heading: string | null; lines: string[] } | null; note: string | null }) {
  const { address } = props;
  return (
    <div className="addressblock">
      <p className="eyebrow">{props.label}</p>
      {address ? (
        <>
          {address.heading && <strong>{address.heading}</strong>}
          <p>
            {address.lines.map((l, i) => (
              <span key={i}>{l}<br /></span>
            ))}
          </p>
        </>
      ) : (
        <p>To be confirmed</p>
      )}
      {props.note && <span className="addressnote">{props.note}</span>}
    </div>
  );
}

function ProductCard(props: {
  line: PublicLine;
  index: number;
  currency: string;
  pricing: boolean;
  approved: boolean;
  onPreview: (d: PublicDecoration) => void;
}) {
  const { line: l, currency, pricing } = props;
  const img = firstImage(l.productSnapshot);
  const category = categoryOf(l.productSnapshot);
  const body = lineBody(l);
  const configs = variantSummary(l.variants);
  const locations = l.decorations.filter((d) => !d.isItemCosts);
  const charges = l.decorations.flatMap((d) => d.charges);
  const each = unitPriceLabel(l, currency);
  return (
    <article className="product" id={`item-${l.id}`}>
      <div className="product-main">
        <div>
          <div className="catalogphoto">
            {img ? <img src={img} alt={lineName(l)} loading="lazy" /> : <span className="nophoto">Photo to follow</span>}
          </div>
        </div>
        <div className="description">
          <p className="eyebrow">
            {String(props.index).padStart(2, "0")}
            {category ? ` / ${category.toUpperCase()}` : ""}
          </p>
          <h2>{lineName(l)}</h2>
          {body && <p className="bodycopy">{body}</p>}
          {/* Quantity and price sit right under the description, not at the
              bottom of the card (Stephanie 2026-10-26). */}
          <div className={pricing ? "price" : "price single"}>
            <div>
              <span>Quantity</span>
              <strong>{l.quantityTotal.toLocaleString("en-US")} <small>units</small></strong>
            </div>
            {pricing && (
              <>
                <div>
                  <span>Unit price</span>
                  <strong>
                    {each?.startsWith("from ") ? (
                      <><small>from </small>{each.slice(5)}</>
                    ) : (
                      each ?? "—"
                    )}
                  </strong>
                </div>
                <div>
                  <span>Item total</span>
                  <strong>{cents(lineTotalCents(toMoneyLine(l)), currency)}</strong>
                </div>
              </>
            )}
          </div>
          {pricing && charges.length > 0 && (
            // Each charge on its own row, readable (Stephanie 2026-10-27:
            // "shown larger and clearer"). Money from the shared rule.
            <div className="charges">
              <p className="chargeshead">Decoration charges — included in the item total</p>
              {charges.map((c, i) => {
                const per = c.billingModel === "per_piece" ? ("per_piece" as const) : ("fixed" as const);
                const qty = chargeQuantity({ billingModel: per, quantity: c.quantity ?? null }, l.quantityTotal);
                const rowCents = lineChargesCents({ unitPrice: 0, quantityTotal: l.quantityTotal, decorationCharges: [{ amount: c.amount, billingModel: per, quantity: c.quantity ?? null }] });
                return (
                  <div className="chargerow" key={c.id ?? i}>
                    <span className="chargename">{chargeLabel(c)}</span>
                    <span className="chargecalc">{qty.toLocaleString("en-US")} × {money(c.amount, currency)}</span>
                    <strong>{cents(rowCents, currency)}</strong>
                  </div>
                );
              })}
            </div>
          )}
          {configs.length > 0 && (
            <div className="detail">
              <span>Colors &amp; sizes</span>
              {configs.map((c) => <strong key={c}>{c}</strong>)}
            </div>
          )}
        </div>
      </div>
      {locations.length > 0 && (
        // Always shown — nothing to click open (Stephanie 2026-10-26).
        <section className="artsection">
          <h3 className="sectionhead">
            Artwork &amp; decoration{" "}
            <span>{locations.length} {locations.length === 1 ? "location" : "locations"}</span>
          </h3>
          {locations.map((d) => (
            <div className="artrow" key={d.id}>
              {d.artwork ? (
                <button
                  type="button"
                  className="artpreview"
                  aria-label={`Preview artwork for ${d.location ?? "this location"}`}
                  onClick={() => props.onPreview(d)}
                >
                  {d.artwork.isImage || d.artwork.previewUrl ? (
                    <img src={d.artwork.isImage ? d.artwork.url : d.artwork.previewUrl!} alt="" />
                  ) : (
                    <span>VIEW FILE</span>
                  )}
                  <small>PREVIEW ↗</small>
                </button>
              ) : (
                <div className="artpreview empty">
                  <span>ARTWORK TO FOLLOW</span>
                </div>
              )}
              <div className="artinfo">
                <div className="arttitle">
                  <strong>{d.location?.trim() || "Decoration"}</strong>
                  <span className="proofstate">{props.approved ? "Approved" : "For review"}</span>
                </div>
                <dl>
                  <div><dt>Method</dt><dd>{d.method ?? "To be confirmed"}</dd></div>
                  <div><dt>Size</dt><dd>{d.size ?? "To be confirmed"}</dd></div>
                  <div><dt>Color / finish</dt><dd>{d.color ?? "To be confirmed"}</dd></div>
                </dl>
                {d.artwork && (
                  <div className="fileline"><span className="filename">{d.artwork.name}</span></div>
                )}
                <small className="uploadnote">Final placement is confirmed in your proof.</small>
              </div>
            </div>
          ))}
        </section>
      )}
    </article>
  );
}

function ShipmentRow(props: { shipment: PublicShipment }) {
  const s = props.shipment;
  const carrier = carrierLabel(s.carrier);
  const when = s.deliveredAt
    ? `Delivered ${longDate(s.deliveredAt)}`
    : s.shippedAt
      ? `Shipped ${longDate(s.shippedAt)}`
      : s.projectedShipDate
        ? `Estimated ship ${longDate(s.projectedShipDate)}`
        : null;
  return (
    <div className="statusrow">
      <span>
        {carrier || "Shipment"}
        {s.trackingNumber && (
          <>
            {" · "}
            {s.trackingUrl ? (
              <a href={s.trackingUrl} target="_blank" rel="noreferrer">{s.trackingNumber}</a>
            ) : (
              s.trackingNumber
            )}
          </>
        )}
        {when && <small> · {when}</small>}
      </span>
      <strong>{SHIPMENT_STATUS_LABEL[s.status]}</strong>
    </div>
  );
}

// Styles transcribed from the CEO's design (other-quote-review…/style.css,
// "multi artworkquote" layout), scoped under .oq so nothing leaks into Flow.
const QUOTE_CSS = `
.oq{min-height:100vh;background:#f6f6f3;color:#111;font:15px/1.5 Arial,Helvetica,sans-serif}
.oq *{box-sizing:border-box}
.oq button,.oq select,.oq input,.oq textarea{font:inherit}
.oq button,.oq summary,.oq select{cursor:pointer}
.oq button:focus-visible,.oq summary:focus-visible,.oq a:focus-visible,.oq select:focus-visible,.oq input:focus-visible{outline:3px solid #666;outline-offset:4px}
.oq-top{max-width:1280px;margin:auto;padding:23px 36px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #dcdcd7}
.oq-top img{width:123px;height:53px;object-fit:contain;object-position:left}
.oq button.text{border:0;background:none;color:#444;font-size:13px}
.oq-main{max-width:1280px;padding:30px 36px;margin:auto}
.oq-empty{max-width:640px;margin:auto;padding:120px 24px;text-align:center}
.oq-empty h1{font-size:32px;margin:0 0 12px}
.oq-banner{border-radius:8px;padding:12px 16px;font-size:14px;margin:0 0 20px;border:1px solid #ddd;background:#fff}
.oq-banner.ok{border-color:#b9d3b9;background:#f1f8f1}
.oq-banner.warn{border-color:#e7c9a5;background:#fdf5ea}
.oq .intro{display:flex;justify-content:space-between;align-items:center;gap:16px}
.oq .eyebrow{font-size:11px;font-weight:700;letter-spacing:.12em;color:#61615b;margin:0 0 10px}
.oq .eyebrow span{margin:0 10px;color:#aaa}
.oq h1{font-size:40px;letter-spacing:-1.6px;line-height:1.1;margin:0 0 10px}
.oq .sub{color:#686862;margin:0;white-space:pre-line}
.oq .badge{border:1px solid #ccc;padding:7px 12px;border-radius:20px;font-size:12px;background:#fff;white-space:nowrap}
.oq .facts{display:grid;grid-template-columns:1.3fr 1fr 1.3fr 1fr;border-top:1px solid #ddd;border-bottom:1px solid #ddd;margin:24px 0 28px;padding:16px 0;gap:15px}
.oq .facts span,.oq .price span{display:block;font-size:12px;color:#686862;margin-bottom:4px}
.oq .facts strong{font-size:14px}
.oq .billing-shipping{display:grid;grid-template-columns:1fr 1fr;gap:16px 32px;padding:20px 24px;margin:0 0 26px;background:#fff;border:1px solid #ddd;border-radius:8px;break-inside:avoid}
.oq .addressblock+.addressblock{border-left:1px solid #e6e6e2;padding-left:32px}
.oq .addressblock .eyebrow{margin-bottom:7px}
.oq .addressblock strong{font-size:14px}
.oq .addressblock>p:not(.eyebrow){font-size:13px;line-height:1.65;color:#555;margin:4px 0 10px}
.oq .addressnote{font-size:12px;color:#555}
.oq .addresshelp{grid-column:1/-1;border-top:1px solid #eee;padding-top:12px;margin:0;font-size:11px;color:#666}
.oq .layout{display:grid;grid-template-columns:minmax(0,1fr) 320px;gap:28px}
.oq .sectionhead{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
.oq .sectionhead h2{font-size:17px;margin:0}
.oq .sectionhead span{font-size:12px;color:#777}
.oq .grouphead{font-size:13px;letter-spacing:.06em;text-transform:uppercase;color:#61615b;margin:22px 0 10px}
.oq .product{background:#fff;border:1px solid #ddd;border-radius:10px;overflow:hidden;margin-bottom:18px;scroll-margin-top:20px}
.oq .product-main{display:grid;grid-template-columns:200px 1fr;gap:22px;padding:20px}
.oq .catalogphoto{aspect-ratio:1;background:#f2f2ee;border-radius:5px;overflow:hidden;display:grid;place-items:center}
.oq .catalogphoto img{width:100%;height:100%;object-fit:contain}
.oq .nophoto{font-size:12px;color:#666}
.oq .description h2{font-size:23px;line-height:1.15;letter-spacing:-.6px;margin:0 0 13px}
.oq .bodycopy{font-size:15px;line-height:1.55;margin:0 0 10px;white-space:pre-line}
.oq .detail{border-top:1px solid #e6e6e2;padding-top:12px}
.oq .detail span{display:block;font-size:11px;color:#777}
.oq .detail strong{display:block;font-size:13px}
.oq details{border-bottom:1px solid #d9d9d4;padding:18px 0}
.oq summary{font-weight:600;font-size:14px}
.oq summary span{float:right;font-size:12px;font-weight:400;color:#777}
.oq .terms p{font-size:13px;color:#666}
.oq .artsection{padding:15px 20px;border-top:1px solid #eee;border-bottom:0}
.oq .terms{border-bottom:1px solid #d9d9d4;padding:18px 0}
.oq .sectionhead{font-weight:600;font-size:14px;margin:0 0 6px}
.oq .sectionhead span{float:right;font-size:12px;font-weight:400;color:#777}
.oq .artsection .sectionhead{font-size:13px}
.oq .artrow{display:grid;grid-template-columns:130px 1fr;gap:16px;margin-top:18px;padding-top:16px;border-top:1px solid #eee}
.oq .artpreview{aspect-ratio:1.25;background:#f3f2ed;border:1px solid #ddd;border-radius:5px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;padding:8px;overflow:hidden}
.oq .artpreview img{max-width:100%;max-height:70%;object-fit:contain}
.oq .artpreview span{font-size:11px;font-weight:800;letter-spacing:.02em;text-align:center}
.oq .artpreview small{font-size:11px;color:#666}
.oq .artpreview.empty{cursor:default}
.oq .arttitle{display:flex;justify-content:space-between;gap:10px}
.oq .arttitle strong{font-size:13px}
.oq .proofstate{font-size:11px;color:#555}
.oq .artinfo dl,.oq .artdl{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin:12px 0}
.oq .artinfo dt,.oq .artdl dt{font-size:11px;color:#666}
.oq .artinfo dd,.oq .artdl dd{font-size:12px;margin:0}
.oq .fileline{display:flex;flex-wrap:wrap;gap:10px;align-items:center}
.oq .filename{font-size:11px;color:#666;overflow-wrap:anywhere}
.oq .uploadnote{display:block;font-size:11px;color:#666;margin-top:8px}
.oq .price{display:grid;grid-template-columns:1fr 1fr 1fr;border:1px solid #e5e5e0;border-radius:8px;padding:11px 14px;margin:6px 0 16px;background:#fbfbf9}
.oq .price.single{grid-template-columns:1fr}
.oq .price div:last-child{text-align:right}
.oq .price div:nth-child(2):not(:last-child){text-align:center}
.oq .price.single div:last-child{text-align:left}
.oq .price strong{font-size:21px;letter-spacing:-.4px}
.oq .price small{font-size:12px;font-weight:400;color:#777}
.oq .charges{margin:-6px 0 16px;border:1px solid #e5e5e0;border-radius:8px;padding:10px 14px;background:#fff}
.oq .chargeshead{margin:0 0 4px;font-size:12px;color:#686862}
.oq .chargerow{display:grid;grid-template-columns:1fr auto 110px;gap:12px;align-items:baseline;padding:5px 0;font-size:15px;color:#111}
.oq .chargerow+.chargerow{border-top:1px solid #f0f0ec}
.oq .chargecalc{color:#555;font-size:14px;font-variant-numeric:tabular-nums}
.oq .chargerow strong{text-align:right;font-variant-numeric:tabular-nums}
.oq .services{padding:18px 20px}
.oq .servicerow,.oq .statusrow{display:flex;justify-content:space-between;gap:12px;font-size:14px;padding:8px 0;border-top:1px solid #eee}
.oq .servicenote{display:block;margin-top:2px;font-size:13px;color:#777;white-space:pre-line}
.oq .statusrow small{color:#777}
.oq .statusrow a{color:#111}
.oq .big{font-size:24px;letter-spacing:-.5px}
.oq .status{background:#fff;border:1px solid #ddd;border-radius:10px;padding:18px 20px;margin-bottom:18px}
.oq .paybox{margin-top:6px}
.oq .summary{background:#fff;border:1px solid #d7d7d2;border-radius:10px;padding:23px}
.oq .itemnav{margin:14px 0 20px}
.oq .itemnav label{display:block;font-size:11px;color:#777;margin-bottom:5px}
.oq .itemnav select{width:100%;max-width:100%;padding:9px 7px;border:1px solid #ddd;background:#fafaf8;border-radius:4px;font-size:12px}
.oq .money{display:flex;justify-content:space-between;font-size:14px;margin:16px 0;color:#555}
.oq .money strong{color:#222;font-weight:500}
.oq .total{border-top:1px solid #ddd;padding-top:18px;margin-top:20px;display:flex;align-items:center;justify-content:space-between}
.oq .total strong{font-size:26px;letter-spacing:-.7px}
.oq .total small{display:block;color:#666;font-size:11px}
.oq .shipping{font-size:11px;color:#74746f;margin:14px 0 20px}
.oq .approvedline{font-size:13px;font-weight:600;margin:14px 0 0}
.oq .fine{font-size:11px;color:#777;margin:12px 0 0}
.oq .contact{display:flex;gap:11px;padding:22px 8px}
.oq .avatar{width:34px;height:34px;flex-shrink:0;display:grid;place-items:center;background:#e6e6df;border-radius:50%;font-size:11px}
.oq .contact span{display:block;font-size:11px;color:#777}
.oq .contact strong{display:block;font-size:14px;margin:3px 0}
.oq .contact a{display:block;color:#555;text-decoration:none;font-size:12px;margin:3px 0}
.oq aside{position:sticky;top:20px;align-self:start}
.oq .primary,.oq .secondary{display:block;width:100%;padding:13px 10px;border-radius:5px;font-size:13px}
.oq .primary{background:#111;color:#fff;border:1px solid #111}
.oq .primary:disabled{opacity:.35;cursor:not-allowed}
.oq .secondary{background:#fff;border:1px solid #ddd;margin-top:9px;color:#111}
.oq .reviewlink{text-align:center;text-decoration:none}
.oq .bottomapproval{display:grid;grid-template-columns:1fr 1fr;gap:50px;margin-top:30px;padding:30px;border:1px solid #ccc;border-radius:8px;background:#fff;scroll-margin-top:20px;break-inside:avoid}
.oq .bottomapproval h2{font-size:28px;letter-spacing:-.8px;margin:0 0 10px}
.oq .bottomapproval p{font-size:14px;color:#666}
.oq .bottomapproval p.fine{font-size:11px;color:#777}
.oq .accept{display:flex;gap:10px;font-size:13px;line-height:1.6;margin:0 0 18px;color:#111}
.oq input[type=checkbox]{accent-color:#111;width:16px;height:16px;flex-shrink:0;margin-top:3px}
.oq .namefield{display:block;margin:0 0 14px}
.oq .namefield span{display:block;font-size:11px;color:#777;margin-bottom:5px}
.oq .namefield input{width:100%;padding:10px 12px;border:1px solid #ccc;border-radius:5px;background:#fff}
.oq footer{margin-top:38px;padding-top:20px;border-top:1px solid #d9d9d4;display:flex;justify-content:space-between;align-items:center;font-size:12px}
.oq footer span{font-weight:700}
.oq footer p{font-size:11px;color:#666;text-align:right}
.oq dialog{border:1px solid #ddd;border-radius:12px;max-width:520px;width:90%;padding:30px;color:#111}
.oq dialog::backdrop{background:#0007}
.oq .close{float:right;border:0;background:none;font-size:24px;line-height:1}
.oq dialog p{color:#555;margin:0 0 14px}
.oq dialog textarea{width:100%;min-height:110px;margin:6px 0 14px;padding:12px;border:1px solid #ccc;border-radius:5px}
.oq dialog h2{font-size:24px;letter-spacing:-.5px;margin:0 0 8px}
.oq .fullart{display:block;max-width:100%;max-height:340px;margin:12px auto;object-fit:contain;background:#f3f2ed;border-radius:6px}
html{scroll-behavior:smooth}
@media(max-width:1000px){.oq .product-main{grid-template-columns:160px 1fr;gap:17px}.oq .layout{gap:18px;grid-template-columns:minmax(0,1fr) 285px}.oq .description h2{font-size:21px}}
@media(max-width:780px){.oq-top{padding:17px 20px}.oq-main{padding:24px 20px}.oq .layout{display:flex;flex-direction:column}.oq aside{position:static;order:-1}.oq .summary{padding:18px;margin-top:8px}.oq .contact{padding:16px 4px 0}.oq .itemnav{margin:10px 0}.oq .money{display:inline-flex;width:49%;padding-right:12px;margin:7px 0;font-size:12px}.oq .total{margin-top:8px;padding-top:10px}.oq .shipping{margin:8px 0}.oq .fine{margin-top:6px}.oq .facts{grid-template-columns:1fr 1fr;gap:16px;margin-bottom:15px}.oq h1{font-size:32px}.oq .badge{font-size:10px}.oq .product-main{grid-template-columns:170px 1fr}.oq footer{margin-top:24px}}
@media(max-width:600px){.oq .bottomapproval{grid-template-columns:1fr;gap:20px;padding:22px}.oq .artrow{grid-template-columns:100px 1fr;gap:12px}.oq .artinfo dl,.oq .artdl{grid-template-columns:1fr}.oq .sectionhead span{float:none;display:block}}
@media(max-width:540px){.oq .billing-shipping{grid-template-columns:1fr;padding:18px;gap:16px}.oq .addressblock+.addressblock{border-left:0;border-top:1px solid #eee;padding:16px 0 0}}
@media(max-width:450px){.oq .product-main{grid-template-columns:1fr}.oq .catalogphoto{max-width:250px;width:100%;margin:auto}.oq .description h2{font-size:24px}.oq summary span{float:none;display:block;margin:5px 0 0}.oq .intro{flex-direction:column;align-items:flex-start}.oq .price strong{font-size:19px}.oq footer{display:block}.oq footer p{text-align:left}}
@media print{
  .oq{background:#fff}
  .oq .noprint,.oq-top button,.oq dialog,.oq .itemnav,.oq .reviewlink,.oq .bottomapproval button,.oq .namefield{display:none!important}
  .oq-main,.oq-top{max-width:none;padding:15px}
  .oq .layout{display:block}
  .oq aside{position:static;margin-top:16px}
  .oq .contact{display:flex}
  .oq .product-main{grid-template-columns:150px 1fr}
  .oq .artrow{grid-template-columns:110px 1fr}
  .oq details,.oq .product,.oq .summary,.oq .artrow,.oq .bottomapproval{break-inside:avoid}
  .oq .billing-shipping{grid-template-columns:1fr 1fr;padding:12px;margin-bottom:18px}
  .oq .addresshelp{display:none}
  .oq footer{margin-top:15px}
  .oq h1{font-size:30px}
}
`;
